import React, { useEffect, useState } from 'react';
import apiClient from '../../api';
import { useNavigate } from 'react-router-dom';
import './Register.css';
import 'font-awesome/css/font-awesome.min.css';
import { useCurrentPlayer } from '../../hooks/useCurrentPlayer';
import { useCurrentTeam } from '../../hooks/useCurrentTeam';
import Breadcrumb from "../Breadcrumb";

const Register = () => {
    const navigate = useNavigate();
    const { player: currentPlayer, loading: authLoading } = useCurrentPlayer();
    const { team } = useCurrentTeam();

    const [formData, setFormData] = useState({
        name: '',
        lastName: '',
        email: '',
        password: '',
        memberNumber: '',
        teamCategory: ''
    });

    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isRegistered, setIsRegistered] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!authLoading) {
            if (!currentPlayer) {
                navigate('/login');
                return;
            }
            const role = currentPlayer.role || currentPlayer.player?.role || '';
            const allowedRoles = ['ROLE_CAPTAIN', 'ROLE_COORDINATOR', 'ROLE_ADMIN'];

            if (!allowedRoles.includes(role)) {
                navigate('/dashboard');
            }
        }
    }, [currentPlayer, authLoading, navigate]);

    const validateForm = () => {
        if (!formData.name.trim()) return 'El nombre es requerido';
        if (!formData.lastName.trim()) return 'El apellido es requerido';
        if (!formData.email.trim()) return 'El email es requerido';
        if (!formData.password) return 'La contraseña es requerida';
        if (!formData.memberNumber.trim()) return 'El número de socia es requerido';
        if (!formData.teamCategory) return 'Debes seleccionar una categoría';
        return null;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const payload = {
                ...formData,
                email: formData.email.toLowerCase().trim(),
                teamId: team?.id
            };
            const response = await apiClient.post('/register', payload);
            if (response.status >= 200 && response.status < 300) {
                setIsRegistered(true);
                setSuccessMessage(`Jugadora registrada correctamente.`);
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Error al registrar jugadora.';
            setError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        setFormData({
            name: '',
            lastName: '',
            email: '',
            password: '',
            memberNumber: '',
            teamCategory: ''
        });
        setIsRegistered(false);
        setSuccessMessage('');
        setError('');
    };

    if (authLoading) return (
        <div className="register-page-wrapper" style={{justifyContent:'center'}}>
            <i className="fa fa-spinner fa-spin fa-2x" style={{color:'var(--brand-primary)'}}></i>
        </div>
    );

    return (
        <div className="register-page-wrapper">
            <div style={{ width: '100%', maxWidth: '500px', alignSelf: 'center' }}>
                <Breadcrumb dark={false} crumbs={[
                    { label: 'Inicio', to: '/dashboard' },
                    { label: 'Registrar Jugadora' },
                ]} />
            </div>

            <div className="auth-card register-card">

                {!isRegistered ? (
                    <>
                        <div className="register-header">
                            <h2>Nueva Jugadora</h2>
                            <p>Añade una nueva integrante al equipo</p>
                        </div>

                        {error && (
                            <div className="error-banner">
                                <i className="fa fa-exclamation-circle"></i> <span>{error}</span>
                            </div>
                        )}

                        <form className="register-form" onSubmit={handleSubmit}>

                            <div className="name-row">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Nombre"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        placeholder="Apellido"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Correo Electrónico"
                                    required
                                    autoCapitalize="none"
                                    inputMode="email"
                                />
                            </div>

                            <div className="form-group">
                                <input
                                    type="text"
                                    name="memberNumber"
                                    value={formData.memberNumber}
                                    onChange={handleChange}
                                    placeholder="Número de Socia"
                                    required
                                />
                            </div>

                            <div className="form-group select-wrapper">
                                <select
                                    name="teamCategory"
                                    value={formData.teamCategory}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="" disabled>Selecciona Categoría</option>
                                    <option value="PRIMERA">Primera</option>
                                    <option value="SEGUNDA">Segunda</option>
                                    <option value="TERCERA">Tercera</option>
                                    <option value="CUARTA">Cuarta</option>
                                </select>
                            </div>

                            <div className="form-group password-wrapper" style={{position:'relative'}}>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Contraseña Provisional"
                                    required
                                    style={{paddingRight: '45px'}}
                                />
                                <button
                                    type="button"
                                    className="toggle-password"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position:'absolute', right:0, top:0, height:'100%', width:'45px',
                                        border:'none', background:'transparent', cursor:'pointer', color:'var(--text-muted)'
                                    }}
                                >
                                    <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                            </div>

                            <button
                                type="submit"
                                className="action-button"
                                disabled={isSubmitting}
                                style={{marginTop: '10px'}}
                            >
                                {isSubmitting ? (
                                    <span><i className="fa fa-spinner fa-spin"></i> Guardando...</span>
                                ) : (
                                    "Registrar Jugadora"
                                )}
                            </button>
                        </form>
                    </>
                ) : (
                    <div className="success-state">
                        <div className="success-icon">
                            <i className="fa fa-check"></i>
                        </div>
                        <h3>¡Registro Completado!</h3>
                        <p style={{color: 'var(--text-muted)', marginBottom: '20px'}}>
                            {successMessage}
                        </p>

                        <div className="success-actions">
                            <button className="action-button" onClick={handleReset}>
                                <i className="fa fa-plus"></i> Añadir Otra Jugadora
                            </button>

                            <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
                                Volver al Dashboard
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Register;